import { openEraseDialog } from './erase-control.js';
import type { DesignLayer } from '@pz/scene';
import { el, iconBtn, iconSvg, type DesignerCtx, type IconName, type ToolId } from './panels.js';
import { createColorBlock, createStepper, siblingFace, faceFlags } from './controls.js';
import { openPopover } from './overlay.js';
import { canDelete, run } from './commands.js';
import { anchorVOf, setTextBox } from './text-box.js';
import { fillText } from './texts.js';
import { clamp, round, type LayerPatch } from './state.js';

/**
 * The contextual toolbar: a 44 px strip under the header that appears only
 * when something is selected and carries that kind's own controls, in the
 * reference editor's order.
 *
 * It is a strip, not a panel, because the controls it holds are the ones a
 * shopper reaches for constantly — size, colour, bold, duplicate — and a panel
 * that had to be opened first would put two clicks in front of every one of
 * them. Anything that needs room (alignment, effects, the font list) opens
 * into the rail's panel slot instead, which is what `actions` is for.
 */

export interface ToolbarActions {
  /** Opens a panel in the rail's slot (Poravnanje, Efekti, Tekst, Slike…). */
  openPanel(tool: ToolId): void;
  /** Opens the Fontovi dialog in the panel slot. */
  openFonts(): void;
  /** Starts "replace this image" mode in the Slike panel. */
  replaceImage(layerId: string): void;
  /** Enters crop mode on the canvas for an image layer. */
  cropImage(layerId: string): void;
}

export interface ToolbarHandle {
  readonly element: HTMLElement;
  /** Rebuilds for the current selection; hides itself when there is none. */
  refresh(): void;
  destroy(): void;
}

export function createToolbar(ctx: DesignerCtx, actions: ToolbarActions): ToolbarHandle {
  const bar = el('div', 'pzd-toolbar');
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', ctx.texts.title);
  const strip = el('div', 'pzd-toolbar-strip');
  const overflow = iconBtn('chevronRight', ctx.texts.moreTools, () => {
    strip.scrollBy({ left: Math.round(strip.clientWidth * 0.7), behavior: 'smooth' });
  }, { className: 'pzd-toolbar-more' });
  bar.append(strip, overflow);

  function group(): HTMLElement {
    const box = el('div', 'pzd-tgroup');
    strip.append(box);
    return box;
  }

  function textButton(host: HTMLElement, label: string, onClick: () => void, icon?: IconName): HTMLButtonElement {
    const button = el('button', 'pzd-tbtn');
    button.type = 'button';
    if (icon) button.innerHTML = iconSvg(icon, 17);
    button.append(el('span', undefined, label));
    button.addEventListener('click', onClick);
    host.append(button);
    return button;
  }

  function colorButton(
    host: HTMLElement,
    label: string,
    value: { hex: string } | undefined,
    onPick: (color: { hex: string } | undefined) => void,
    options: { allowNone?: boolean; palette?: { hex: string }[] } = {},
  ): void {
    const button = el('button', 'pzd-tbtn is-color');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.title = label;
    const dot = el('span', 'pzd-tdot');
    dot.style.background = value?.hex ?? 'transparent';
    if (!value) dot.classList.add('is-none');
    button.append(dot, el('span', undefined, label));
    button.addEventListener('click', () => {
      openPopover({
        ctx,
        anchor: button,
        title: label,
        className: 'is-color',
        build: (body) => {
          const block = createColorBlock({
            ctx,
            value,
            ...(options.palette ? { palette: options.palette } : {}),
            ...(options.allowNone ? { allowNone: true } : {}),
            onPick: (color) => {
              onPick(color);
              dot.style.background = color?.hex ?? 'transparent';
              dot.classList.toggle('is-none', !color);
            },
          });
          body.append(block.element);
          return () => block.destroy();
        },
      });
    });
    host.append(button);
  }

  function refresh(): void {
    strip.textContent = '';
    strip.scrollLeft = 0;
    const selected = ctx.selected();
    const many = ctx.selection.size > 1;
    if (!selected || ctx.view.review) {
      bar.hidden = ctx.view.review;
      if (!ctx.view.review) strip.append(el('span', 'pzd-hint', 'Izaberi element da ga urediš'));
      overflow.hidden = true;
      return;
    }
    bar.hidden = false;

    if (many) buildMulti(selected);
    else if (selected.kind === 'text') buildText(selected);
    else if (selected.kind === 'image') buildImage(selected);
    else if (selected.kind === 'shape') buildShape(selected);
    else if (selected.kind === 'qr') buildQr(selected);
    else buildArtwork(selected);

    // The overflow arrow only earns its place when something is out of reach.
    window.setTimeout(() => {
      overflow.hidden = strip.scrollWidth <= strip.clientWidth + 2;
    }, 0);
  }

  // ── Per-kind strips ──────────────────────────────────────────────────────

  function buildText(layer: Extract<DesignLayer, { kind: 'text' }>): void {
    const patch = (changes: LayerPatch, tag?: string): void => {
      ctx.dispatch({ type: 'update', id: layer.id, patch: changes, ...(tag ? { tag } : {}) });
    };
    const first = group();
    textButton(first, ctx.texts.editText, () => actions.openPanel('text'), 'text');
    const font = ctx.fonts.list.find((item) => item.id === layer.fontId);
    textButton(first, fillText(ctx.texts.fontNamed, { name: font?.family ?? '—' }), () => actions.openFonts());

    const sizeGroup = group();
    sizeGroup.append(createStepper({
      label: `${ctx.texts.size} (mm)`,
      value: clamp(layer.sizeMm, ctx.config.text.minSizeMm, ctx.config.text.maxSizeMm),
      min: round(ctx.config.text.minSizeMm, 1),
      max: round(ctx.config.text.maxSizeMm, 1),
      step: 0.5,
      unit: 'mm',
      onCommit: (value) => patch({ sizeMm: round(value, 2) }, `size:${layer.id}`),
    }).element);

    const styleGroup = group();
    colorButton(styleGroup, ctx.texts.textColor, layer.color, (color) => {
      if (color) patch({ color, styleId: undefined });
    });
    for (const axis of ['bold', 'italic'] as const) {
      const flags = faceFlags(font ?? { id: '', family: '', style: '', url: '' });
      const on = flags[axis];
      const target = siblingFace(ctx.fonts.list, layer.fontId, axis, !on);
      const button = iconBtn(axis, axis === 'bold' ? ctx.texts.bold : ctx.texts.italic, () => {
        if (!target) return;
        void ctx.fonts.ensure(target).catch(() => undefined);
        patch({ fontId: target });
      }, { pressed: on, disabled: !target, className: 'pzd-tbtn is-icon' });
      styleGroup.append(button);
    }
    const alignBtn = el('button', 'pzd-tbtn');
    alignBtn.type = 'button';
    alignBtn.innerHTML = iconSvg(alignIcon(layer.align ?? 'center'), 17);
    alignBtn.append(el('span', undefined, ctx.texts.textAlign), chevron());
    alignBtn.addEventListener('click', () => {
      openPopover({
        ctx,
        anchor: alignBtn,
        title: ctx.texts.textAlign,
        build: (body) => {
          const row = el('div', 'pzd-icon-row');
          for (const value of ['left', 'center', 'right'] as const) {
            row.append(iconBtn(alignIcon(value), alignLabel(ctx, value), () => patch({ align: value }), {
              pressed: (layer.align ?? 'center') === value,
            }));
          }
          body.append(row);
          // The reference's second row: the vertical anchor. Our box is exactly
          // as tall as the lines it holds, so this is not an alignment INSIDE
          // the box — it is which edge of it stays still when a line is added,
          // a word retyped or the wrap width dragged.
          const current = anchorVOf(layer);
          const anchors = el('div', 'pzd-icon-row');
          const rows: Array<[IconName, string, 'top' | 'middle' | 'bottom']> = [
            ['alignTop', ctx.texts.anchorTop, 'top'],
            ['alignMiddle', ctx.texts.anchorMiddle, 'middle'],
            ['alignBottom', ctx.texts.anchorBottom, 'bottom'],
          ];
          for (const [icon, label, value] of rows) {
            anchors.append(iconBtn(icon, label, () => {
              setTextBox(ctx, [layer.id], { anchorV: value });
              ctx.refreshPanel();
            }, { pressed: current === value }));
          }
          body.append(el('span', 'pzd-hint', ctx.texts.verticalAnchor), anchors);
        },
      });
    });
    styleGroup.append(alignBtn);

    commonActions(layer);
  }

  function buildImage(layer: Extract<DesignLayer, { kind: 'image' }>): void {
    const first = group();
    textButton(first, ctx.texts.changeImage, () => actions.replaceImage(layer.id), 'swap');
    if (ctx.config.image.allowRemoveBackground && ctx.uploads.removeBackground) {
      textButton(first, ctx.texts.removeBackground, () => actions.openPanel('image'), 'magic');
    }
    textButton(first, 'Gumica', () => openEraseDialog(ctx, layer.id), 'image');
    textButton(first, ctx.texts.cropTool, () => actions.cropImage(layer.id), 'crop');
    commonActions(layer, { fitFill: true });
  }

  function buildShape(layer: Extract<DesignLayer, { kind: 'shape' }>): void {
    const patch = (changes: LayerPatch): void => {
      ctx.dispatch({ type: 'update', id: layer.id, patch: changes });
    };
    const first = group();
    colorButton(first, ctx.texts.fillColor, layer.fill, (color) => patch({ fill: color }), {
      allowNone: Boolean(layer.stroke),
      palette: ctx.config.shape.palette,
    });
    colorButton(first, ctx.texts.lineColor, layer.stroke?.color, (color) => {
      if (!color) {
        patch({ stroke: undefined });
        return;
      }
      patch({ stroke: { color, widthMm: layer.stroke?.widthMm || 0.5 } });
    }, { allowNone: true, palette: ctx.config.shape.palette });
    const weight = group();
    weight.append(createStepper({
      label: ctx.texts.lineWeight,
      value: layer.stroke?.widthMm ?? 0,
      min: 0,
      max: 20,
      step: 0.1,
      unit: 'mm',
      onCommit: (widthMm) => {
        if (widthMm <= 0) {
          patch({ stroke: undefined, ...(layer.fill ? {} : { fill: ctx.config.shape.palette[0] ?? { hex: '#000000' } }) });
          return;
        }
        patch({ stroke: { color: layer.stroke?.color ?? ctx.config.shape.palette[0] ?? { hex: '#000000' }, widthMm } });
      },
    }).element);
    commonActions(layer, { fitFill: true });
  }

  function buildArtwork(layer: DesignLayer): void {
    const first = group();
    if (ctx.config.artwork.allowRecolor) {
      textButton(first, ctx.texts.recolor, () => actions.openPanel('icons'), 'artwork');
    }
    commonActions(layer, { fitFill: true });
  }

  function buildQr(layer: DesignLayer): void {
    const first = group();
    textButton(first, ctx.texts.editQr, () => actions.openPanel('elements'), 'qr');
    commonActions(layer, { scale: false });
  }

  function buildMulti(anchor: DesignLayer): void {
    const first = group();
    first.append(el('span', 'pzd-tcount', fillText(ctx.texts.multiSelected, { n: ctx.selection.size })));
    commonActions(anchor, { fitFill: true });
  }

  /** Duplicate · Delete · smaller/larger · fit/fill · Poravnanje · Efekti. */
  function commonActions(layer: DesignLayer, options: { fitFill?: boolean; scale?: boolean } = {}): void {
    const ids = (): string[] => (ctx.selection.size ? [...ctx.selection] : [layer.id]);
    const editGroup = group();
    editGroup.append(iconBtn('copy', ctx.texts.duplicate, () => run(ctx, { op: 'duplicate', ids: ids() }), {
      className: 'pzd-tbtn is-icon',
    }));
    editGroup.append(iconBtn('trash', ctx.texts.remove, () => run(ctx, { op: 'delete', ids: ids() }), {
      className: 'pzd-tbtn is-icon',
      disabled: !ids().every((id) => canDelete(ctx.store.value().layers.find((item) => item.id === id))),
    }));
    if (options.scale !== false) {
      const sizeGroup = group();
      sizeGroup.append(
        iconBtn('minus', ctx.texts.makeSmaller, () => run(ctx, { op: 'scale', ids: ids(), factor: 0.9 }), {
          className: 'pzd-tbtn is-icon',
        }),
        iconBtn('plus', ctx.texts.makeLarger, () => run(ctx, { op: 'scale', ids: ids(), factor: 1.1 }), {
          className: 'pzd-tbtn is-icon',
        }),
      );
    }
    if (options.fitFill) {
      const fitGroup = group();
      textButton(fitGroup, ctx.texts.fitToZone, () => run(ctx, { op: 'fitToZone', ids: ids(), mode: 'fit' }));
      textButton(fitGroup, ctx.texts.fillZone, () => run(ctx, { op: 'fitToZone', ids: ids(), mode: 'fill' }));
    }
    const panelGroup = group();
    textButton(panelGroup, ctx.texts.alignmentTool, () => actions.openPanel('alignment'), 'alignCenter');
    textButton(panelGroup, ctx.texts.effectsTool, () => actions.openPanel('effects'), 'magic');
  }

  function chevron(): HTMLElement {
    const span = el('span', 'pzd-tchev');
    span.innerHTML = iconSvg('chevron', 13);
    return span;
  }

  refresh();

  return {
    element: bar,
    refresh,
    destroy() {
      bar.remove();
    },
  };
}

function alignIcon(align: 'left' | 'center' | 'right'): IconName {
  return align === 'left' ? 'alignLeft' : align === 'right' ? 'alignRight' : 'alignCenter';
}

function alignLabel(ctx: DesignerCtx, align: 'left' | 'center' | 'right'): string {
  return align === 'left' ? ctx.texts.alignLeft : align === 'right' ? ctx.texts.alignRight : ctx.texts.alignCenter;
}
